import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/utils/analytics';

/**
 * Page name mapping for common routes
 * Maps URL paths to human-readable page names
 */
const PAGE_NAMES: Record<string, string> = {
  '/': 'Home',
  '/login': 'Login',
  '/cases': 'Cases List',
  '/cases/new': 'New Case',
  '/knowledge-base': 'Knowledge Base',
};

/**
 * Get readable page name from current path
 * Handles both static and dynamic routes
 */
const getPageName = (pathname: string): string => {
  // Check exact matches first
  if (PAGE_NAMES[pathname]) {
    return PAGE_NAMES[pathname];
  }

  // Handle dynamic routes
  if (pathname.startsWith('/cases/')) {
    if (pathname.includes('/docs')) return 'Case Documents';
    if (pathname.includes('/letters')) return 'Case Letters';
    if (pathname.includes('/exhibit-lists')) return 'Case Exhibit Lists';
    if (pathname.includes('/drafting')) return 'Case Drafting';
    if (pathname.includes('/petition')) return 'Case Petition';
    if (pathname.includes('/uscis-requests')) return 'Case USCIS Requests';

    // Generic case detail page
    const parts = pathname.split('/');
    if (parts.length === 3) return 'Case Details';
  }

  if (pathname.startsWith('/knowledge-base/')) {
    return 'Knowledge Base Article';
  }

  return 'Unknown Page';
};

/**
 * Hook to automatically track page views
 *
 * Usage: Add `usePageTracking()` to any page component
 *
 * Features:
 * - Automatically tracks when route changes
 * - Maps routes to readable page names
 * - Includes URL path and query parameters
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const pageName = getPageName(location.pathname);

    analytics.trackPageView(pageName, {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [location.pathname, location.search, location.hash]);
};
